import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { AccountsApi } from './api';
import type {
  AuthRequest,
  CreateBrandRequest,
  AddTeamMemberRequest,
  UpdateTeamMemberRequest,
  UpdateAccountRequest,
  SwitchAccountRequest,
  AccountFilters,
  UpdatePhotoRequest,
  UpdateProfileRequest,
} from './types';
import { setStoredAuth, getStoredAuth, clearStoredAuth, setLinkedAccounts } from '@/lib/auth';

const QUERY_KEYS = {
    all: ['auth'] as const,
    list: (filters?: AccountFilters) => [...QUERY_KEYS.all, filters] as const,
    team: () => [...QUERY_KEYS.all, 'team'] as const,
};


export const useAccountSwitch = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data: SwitchAccountRequest) => AccountsApi.switchAccount(data),
      onSuccess: (response) => {
        const currentAuth = getStoredAuth();
        setStoredAuth({
          token: response.access_keys.access_token,
          refreshToken: response.access_keys.refresh_token,
          user_id: response.user_id,
          org_id: response.account_id,
          role: response.role,
          features: response.features,
          identity: currentAuth?.identity,
        })
        // Clear entire cache so all queries refetch with the new brand's token
        queryClient.clear();
      },
    });
};

export const useBusinessLogin = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data: AuthRequest) => AccountsApi.businessLogin(data),
      onSuccess: (response) => {
        // Find the matching linked account to get features
        const matchedAccount = response.linked_accounts?.find(
          (acc) => acc.account_id === response.profile.account_id
        );
        setStoredAuth({
          token: response.access_keys.access_token,
          refreshToken: response.access_keys.refresh_token,
          user_id: response.profile.account_id,
          org_id: response.profile.account_id,
          role: matchedAccount?.role ?? response.role,
          features: matchedAccount?.features,
          identity: response.identity,
        })
        queryClient.setQueryData(QUERY_KEYS.all, response);
      },
    });
};

export const useCreateBrand = () => {
    return useMutation({
      mutationFn: (data: CreateBrandRequest) => AccountsApi.createBrand(data),
      onSuccess: (response) => {
        if (response.linked_accounts) {
          setLinkedAccounts(response.linked_accounts.map(({ access_keys, ...rest }) => rest));
        }
      },
    });
};

export const useGetTeam = () => {
    return useQuery({
        queryKey: QUERY_KEYS.team(),
        queryFn: () => AccountsApi.getTeam(),
    });
};

export const useGetTeamMember = (userId: string) => {
    return useQuery({
        queryKey: QUERY_KEYS.team(),
        queryFn: () => AccountsApi.getTeamMember(userId),
        enabled: !!userId,
    });
};

export const useAddTeamMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data: AddTeamMemberRequest) => AccountsApi.addTeamMember(data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.team});
      },
    });
};

export const useUpdateTeamMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, data }: { id: string; data: UpdateTeamMemberRequest }) => AccountsApi.updateTeamMember(id, data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.team});
      },
    });
};

export const useDeleteTeamMember = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (userId: string) => AccountsApi.deleteTeamMember(userId),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.team});
      },
    });
};

export const useGetAccount = (filters?: AccountFilters) => {
    return useQuery({
        queryKey: QUERY_KEYS.list(filters),
        queryFn: () => AccountsApi.getAccount(filters),
    });
};

export const useUpdateAccount = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (data: UpdateAccountRequest) => AccountsApi.updateAccount(data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all});
      },
    });
};

export const useBusinessLogout = () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: () => AccountsApi.businessLogout(),
      onSuccess: () => {
        clearStoredAuth();
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all});
      },
    });
};

export const useGetTokenExpiry = () => {
    return useQuery({
        queryKey: QUERY_KEYS.list(),
        queryFn: () => AccountsApi.getTokenExpiry(),
    });
};

export const useUpdateAccountPhoto = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePhotoRequest }) => AccountsApi.updateAccountPhoto(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all});
    },
  });
};

export const useUpdateAccountProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProfileRequest }) => AccountsApi.updateAccountProfile(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all});
    },
  });
};